/**
 * ============================================================================
 * REDUX STORE — wiring only. Business logic lives in slices/api, never here.
 * ============================================================================
 */

import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import dashboardReducer, { persistDashboardState } from 'src/features/dashboard/dashboardSlice';
import { executiveSummaryApi } from 'src/features/api/executiveSummaryApi';
import { metricsApi } from 'src/features/api/metricsApi';
import { metricDefinitionsApi } from 'src/features/api/metricDefinitionsApi';
import { weekNumbersApi } from 'src/features/api/weekNumbersApi';

const rootReducer = {
  dashboard: dashboardReducer,
  [executiveSummaryApi.reducerPath]: executiveSummaryApi.reducer,
  [metricsApi.reducerPath]: metricsApi.reducer,
  [metricDefinitionsApi.reducerPath]: metricDefinitionsApi.reducer,
  [weekNumbersApi.reducerPath]: weekNumbersApi.reducer,
};

/**
 * Store factory — use in tests to get an isolated store per test
 * (avoids shared state and localStorage writes across test files).
 */
export function setupStore() {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(executiveSummaryApi.middleware)
        .concat(metricsApi.middleware)
        .concat(metricDefinitionsApi.middleware)
        .concat(weekNumbersApi.middleware),
  });
}

export const store = setupStore();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Persist dashboard slice — reference equality check skips unrelated actions
let lastDashboardRef: RootState['dashboard'] | undefined;
store.subscribe(() => {
  const current = store.getState().dashboard;
  if (current !== lastDashboardRef) {
    lastDashboardRef = current;
    persistDashboardState(current);
  }
});
